import { Component, Inject, Injector, OnDestroy, ViewChild, TemplateRef, Input, Output, EventEmitter } from '@angular/core';

import { Observable, Subscription, filter, switchMap } from 'rxjs';
import { MessageService } from 'primeng/api';

import { TabComponent } from '../../../Layout/components/Tab/tab.component';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { FilterService } from '../../../Filter/services/abstract-filter.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { ChipModel } from '../../../Forms/models/chip.model';
import { TwitterAvatarSize } from '../../../Misc/pipes/TwitterAvatarSize/twitter-avatar-size.pipe';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { TargetingResultModel } from '../../../../Discover/Targeting/models/targeting-result.model';
import { TargetingListService } from '../../../../Discover/Targeting/services/targeting-list.service';
import { TargetingSearchService } from '../../../../Discover/Targeting/services/targeting-search.service';
import { ListPickerService } from '../../../RhpContexts/services/list-picker.service';
import { ListPickerDialogComponent } from '../../../RhpContexts/components/ListPickerDialog/list-picker-dialog.component';
import { RightSidebarComponent } from '../../../Layout/components/RightSidebar/right-sidebar.component';
import { OutletModel } from '../../../../Discover/Targeting/models/outlet.model';
import { FilterGroupModel } from '../../../Filter/models/filter-group.model';
import { TargetingListList } from '../../../../Discover/Targeting/models/targeting-list-list.model';
import { AutocompleteCustomOption, AppearCondition } from '../../../Forms/components/Autocomplete/autocomplete-custom-option.model';
import { ChipContainerComponent } from '../../../Forms/components/ChipContainer/chip-container.component';
import { AutocompleteComponent } from '../../../Forms/components/Autocomplete/autocomplete.component';
import { PrivateDataModel } from '../../../../Discover/Targeting/models/private-data.model';
import { TargetingRhpContextsService } from '../../../TargetingShared/services/targeting-rhp-contexts.service';
import { AffiliatesService } from '../../services/affiliates.service';
import { CommonApiErrorEnum } from '../../../../api/genericApi/enums/common-api-error.enum';
import { TargetingList } from '../../../../Discover/Targeting/models/targeting-list.model';
import { animate, AnimationEvent, keyframes, style, transition, trigger } from '@angular/animations';
import { UserPreferencesService } from '@Account/UserPreferences/services/user-preferences.service';
import { toolbarAnimation } from '@Common/Misc/animations/toolbar-animation';
import { ToolbarStateService } from '@Common/TargetingShared/services/toolbar-state.service';

class OutletGroup {
    label: string;
    outlets: TargetingResultModel[];
}

@Component({
    selector: 'agpr-affiliates-tab',
    templateUrl: './affiliates-tab.component.html',
    styleUrls: [
        './affiliates-tab.component.scss',
        '../../../TargetingShared/components/EntityDetails/entity-details.component.scss', // Header card styling
        '../../../../Discover/Targeting/components/TargetingResults/targeting-results.component.scss' // List picker styling
    ],
    providers: [
        ToolbarStateService,
        AffiliatesService,
        {
            provide: TabComponent,
            useExisting: AffiliatesTabComponent
        },
        {
            provide: 'PaginatorServiceInterface',
            useExisting: AffiliatesService
        },
        {
            provide: FilterService,
            useExisting: AffiliatesService
        },
        {
            provide: 'SortOrderServiceInterface',
            useExisting: AffiliatesService
        },
        {
            provide: 'affiliatesListPickerService',
            useClass: ListPickerService
        },
        {
            provide: ListPickerService,
            useExisting: 'affiliatesListPickerService'
        }
    ],
    animations: [
        trigger('fade', [
            transition('* => true', [
                animate('300ms ease-in-out', keyframes([
                    style({
                        opacity: 0
                    }),
                    style({
                        opacity: 1
                    })
                ]))
            ]),
            transition('true => *', [
                animate('300ms ease-in-out', keyframes([
                    style({
                        opacity: 1
                    }),
                    style({
                        opacity: 0
                    })
                ]))
            ])
        ]),
        toolbarAnimation
    ],
    standalone: false
})

export class AffiliatesTabComponent extends TabComponent implements OnDestroy {

    @Input() isActive: boolean = true;
    @Input() hideBusinessCard: boolean = false;

    @ViewChild('tabHeader') declare _header: TemplateRef<any>;
    public get header(): TemplateRef<any> {
        return this._header;
    }
    public set header(value: TemplateRef<any>) {
        this._header = value;
    }
    @ViewChild('listChipsComponent') public listChipsComponent: ChipContainerComponent;
    @ViewChild('listBuilderDropdown') public listBuilderDropdown: AutocompleteComponent;

    @Output() showRhpTakeOver?: EventEmitter<any> = new EventEmitter<any>();

    private subscriptions: Subscription[] = [];

    id: string = 'affiliatesTab';
    results: TargetingResultModel[];
    organizedResults: OutletGroup[];
    selectedResults: TargetingResultModel[] = [];
    total: number;
    allSelected: boolean = false;
    pageNumber: number = 1;
    primaryContact: TargetingResultModel;
    selectedOutlet: OutletModel;
    contactPrivateData: PrivateDataModel;
    outletPrivateData: PrivateDataModel;

    targetingLists: TargetingListList[];
    selectedListsChips: ChipModel[] = [];
    selectedLists: TargetingListList[] = [];
    targetingLists$: Observable<TargetingListList[]>;
    freshCreatedId: number;
    workingListsLocked: boolean = false;

    loading = false;
    chipsVisible = false;
    sidebarExpanded: boolean = false;
    readonly twitterAvatarSizes = TwitterAvatarSize;
    readonly buttonTypes = ButtonTypesEnum;

    readonly listBuilderOptions: AutocompleteCustomOption[] = [{
        label: 'Discover.listBuilder.createList',
        action: (listName: string) => {
            if (listName.length === 0) {

                return;
            }

            this.targetingListService.isNameDuplicated$(listName).pipe(
                switchMap((duplicate) => {
                    if (duplicate) {
                        throw new Error(CommonApiErrorEnum.duplicateName);
                    } else {
                        const list = new TargetingList({name: listName});
                        return this.targetingListService.create(list);
                    }
                })
            ).subscribe({
                next: (id) => {
                    this.freshCreatedId = id;
                    this.newlyAddedList();
                },
                error: () => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.dialogs.newList.duplicate',
                    });
                }
            });
        },
        appearCondition: AppearCondition.noExactMatchInSuggestions,
        clearOnSelect: false
    }];

    constructor(
        public affiliatesService: AffiliatesService,
        public targetingSearchService: TargetingSearchService,
        public coreDialogService: CoreDialogService,
        public targetingListService: TargetingListService,
        public rhp: RightSidebarComponent,
        public targetingRhpContextsService: TargetingRhpContextsService,
        private messageService: MessageService,
        public injector: Injector,
        private userPreferencesService: UserPreferencesService,
        private toolbarStateService: ToolbarStateService,
        @Inject('affiliatesListPickerService') public listPickerService: ListPickerService
    ) {
        super();
        this.targetingLists$ = this.targetingListService.all$;
        this.listPickerService.useLocking = true;
        this.subscriptions.push(
            this.rhp.selectedTabIndexChange.subscribe(() => this.selectNone()),
            this.affiliatesService.getResults().subscribe(results => {
                this.results = results;
                this.organizeAffiliates();
            }),
            this.affiliatesService.getFilters().subscribe(filters => {
                this.chipsVisible = !!filters.find(chipFilter => {
                    if (chipFilter instanceof FilterGroupModel) {
                        return chipFilter.children.find(x => x.chips && x.chips.length > 0);
                    } else {
                        return chipFilter.chips && chipFilter.chips.length > 0;
                    }
                });
            }),
            this.affiliatesService.getSearchLoading().subscribe(loading => this.loading = loading),
            this.targetingRhpContextsService.getOutlet().subscribe(outlet => {
                this.selectedOutlet = outlet.outlet;
                this.organizeAffiliates();
                this.selectNone();
            }),
            this.targetingRhpContextsService.getOutletPrivateData().pipe(filter(x => !!x)).subscribe(privateData => {
                this.outletPrivateData = privateData;
            }),
            this.affiliatesService.getItemCount().subscribe(total => this.total = total),
            this.affiliatesService.getPageCriteria().subscribe((pageCriteria) => {
                this.pageNumber = pageCriteria?.page;
                this.selectNone();
            }),
            this.listPickerService.getWorkingLists().pipe(
                filter(x => !!x))
                .subscribe(lists => {
                    this.selectedLists = lists;
                    this.selectedListsChips = lists.map(list => ({
                        label: list.name
                    }));
                }),
            this.listPickerService.getWorkingListsLock().subscribe(lock => {
                this.workingListsLocked = lock;
            }),
            this.targetingListService.all$.subscribe(
                (lists) => {
                    this.targetingLists = lists;
                    // remove lists that have been deleted
                    this.selectedLists = this.selectedLists.filter((selectedList) => {
                        return !!this.targetingLists.find(list => selectedList.id === list.id);
                    });

                    this.newlyAddedList();
                }
            ),
            this.userPreferencesService.getUserAppPreferences().subscribe(prefs => {
                this.toolbarStateService.toolbarAnimationDisabled = !prefs.generalPrefs.accessibility.collapseToolbars;
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    newlyAddedList() {
        if (this.freshCreatedId) {
            const freshCreatedList = this.targetingLists.find(x => x.id === this.freshCreatedId);
            this.listPickerService.addWorkingList(freshCreatedList);
            this.listPickerService.setWorkingListsLock(true);
            this.freshCreatedId = undefined;
        }
    }

    updateListBuilder() {
        this.listPickerService.setWorkingLists(this.selectedLists);
        this.selectedListsChips = this.selectedLists.map(workingList => ({
            label: workingList.name
        }));

        this.listPickerService.setWorkingListsLock(true);
    }

    openListPicker(addMode: boolean = true) {
        this.coreDialogService.open(ListPickerDialogComponent, {
            inputs: {
                addMode: addMode,
                outletUuid: this.selectedOutlet.id
            },
            outputs: {}
        },
        this.injector);
    }

    addToList() {
        this.listPickerService.setSelectedEntities(this.selectedResults);
        if (this.selectedListsChips?.length > 0) {
            this.listPickerService.addSelectedEntitiesToWorkingLists(this.selectedOutlet.id);
        } else {
            this.openListPicker();
        }
    }

    clearListChips() {
        this.listChipsComponent.chipPopover.hide();
        this.listPickerService.clearWorkingLists();
    }

    toggleSelectAll(): void {
        if (this.results.length <= this.selectedResults.length) {
            this.selectNone();
        } else {
            this.selectAllInPage();
        }
    }

    selectNone(): void {
        this.listPickerService.setAllResultsSelected(false);
        this.allSelected = false;
        this.selectedResults = [];
    }

    selectAllInPage(): void {
        this.selectedResults = [];
        this.organizedResults.forEach(outletGroups => {
            outletGroups.outlets.forEach(outlet => {
                if (outlet.id !== this.primaryContact?.id) {
                    this.selectedResults.push(outlet);
                }
            });
        });

        if (this.primaryContact) {
            this.selectedResults.push(this.primaryContact);
        }
    }

    selectAllResults() {
        this.listPickerService.setAllResultsSelected(true, this.total);
        this.allSelected = true;
    }

    // Groups by product type with a special category for the parent outlet
    organizeAffiliates(): OutletGroup[]  {
        if (!this.results || !this.selectedOutlet) {
            this.organizedResults = null;
            return;
        }

        this.organizedResults = [];

        this.results.forEach(outlet => {
            if (outlet.id === this.selectedOutlet.parentOutletId) {
                this.organizedResults.push({
                    label: 'Discover.targeting.rhp.tabs.affiliates.parentOutlet',
                    outlets: [outlet]
                });
            } else {
                const productType = !!outlet.outletModel.productType ? outlet.outletModel.productType.displayString : 'Common.other';
                let group = this.organizedResults.find(x => x.label === productType);
                if (!group) {
                    group = {
                        label: productType,
                        outlets: []
                    };
                    this.organizedResults.push(group);
                }
                group.outlets.push(outlet);
            }
        });

        this.organizedResults = this.organizedResults.sort((x, y) => {
            if (x.label === 'Discover.targeting.rhp.tabs.affiliates.parentOutlet') {
                return -1;
            }
            if (x.label === 'Common.other') {
                return 1;
            }
            return x.label.localeCompare(y.label);
        });
    }

    /**
     * Delegates scroll updates to the ToolbarStateService, which handles
     * collapsing or expanding the toolbar based on scroll movement.
     *
     * @param scrollTop - The current vertical scroll position of the container.
     */
    handleScroll(scrollTop: number) {
        this.toolbarStateService.onScroll(scrollTop);
    }

    /**
     * Triggered when the toolbar animation starts.
     */
    onAnimationStart(event: AnimationEvent) {
        this.toolbarStateService.onAnimationStart(event);
    }

    /**
     * Triggered when the toolbar animation ends.
     */
    onAnimationDone(event: AnimationEvent) {
        this.toolbarStateService.onAnimationDone(event);
    }
}
